'use client';
import Script from 'next/script';
import { useEffect, useRef } from 'react';

import {
  GTM_ACTION_CLICK,
  GTM_EVENT_CHAT_DOWNLOAD_CLICK,
  GTM_EVENT_CHAT_FEEDBACK_CLICK,
  GTM_EVENT_CHAT_FEEDBACK_INPUT,
  GTM_EVENT_CHAT_TEXT_CLICK,
  GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
  GTM_EVENT_USER_INTERACTIONS,
} from '@shared/utils/constants/gtm';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { useMounted } from '@shared/utils/hooks/use-mounted';

import { Loader } from '../../components/loader/loader';
import { type BettyBotBlock } from '../../utils/data/graphql/_generated/gql-generated';

// eslint-disable-next-line import/no-unassigned-import
import './betty-bot-block-overrides.scss';
import { MembershipLevel } from '../../utils/enums/membership';

import styles from './betty-bot-block.module.scss';

type EventName =
  | 'downloadChat'
  | 'enterSend'
  | 'feedbackSubmitBtn'
  | 'learnMore'
  | 'learnMoreTopicLink'
  | 'sendChat'
  | 'thumbsDown'
  | 'thumbsUp';

type HTMLIndex = 'all' | number;

type ElementWatchItem = {
  eventName: EventName;
  eventType: keyof HTMLElementEventMap;
  index: HTMLIndex;
  selector: string;
};

export const BettyBotBlockClient = ({
  blockData,
  membershipLevel,
  userId,
}: {
  blockData: BettyBotBlock;
  membershipLevel: Nullable<MembershipLevel>;
  userId: string;
}) => {
  const isMounted = useMounted();
  const thumbRef = useRef('');
  const learnMoreTopicLinkIndex = useRef<null | number>(null);

  let apiToken;
  let isMember = false;
  switch (membershipLevel) {
    case MembershipLevel.COT:
      apiToken = blockData.BettyCOTToken;
      isMember = true;
      break;
    case MembershipLevel.MDRT:
      apiToken = blockData.BettyMDRTToken;
      isMember = true;
      break;
    case MembershipLevel.TOT:
      apiToken = blockData.BettyTOTToken;
      isMember = true;
      break;
    default:
      apiToken = blockData.BettyEveryoneToken;
      break;
  }

  if (!blockData.BettyApiUrl || !apiToken) {
    return null;
  }

  const shouldShowUpload =
    blockData.AllowUploads && (!blockData.HideFileUploadOnPublic || isMember);

  const chatToGTM = (chatAction: string) => {
    if (chatAction === 'sendChat' || chatAction === 'enterSend') {
      const chatInput = document.querySelector('#input') as HTMLTextAreaElement | null;
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_CHAT_TEXT_CLICK,
        label: chatInput?.value,
      });
    } else if (chatAction === 'downloadChat') {
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_CHAT_DOWNLOAD_CLICK,
      });
    } else if (chatAction === 'thumbsUp' || chatAction === 'thumbsDown') {
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_CHAT_FEEDBACK_CLICK,
        label: chatAction === 'thumbsUp' ? 'Positive' : 'Negative',
      });
    } else if (chatAction === 'feedbackSubmitBtn') {
      const inputValue = document.querySelectorAll('.feedback-input')[0] as
        | HTMLTextAreaElement
        | undefined;

      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_CHAT_FEEDBACK_INPUT,
        label: thumbRef.current === 'thumbsUp' ? 'Positive' : 'Negative',
        link_label: inputValue?.value || '',
      });
    } else if (chatAction === 'learnMore') {
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
        label: 'Chat_LearnMore_Topics',
      });
    } else if (chatAction === 'learnMoreTopicLink') {
      const index = learnMoreTopicLinkIndex.current;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const _link =
        // eslint-disable-next-line no-negated-condition
        index !== null
          ? (document.querySelectorAll('.referenceLink')[index] as HTMLAnchorElement | undefined)
          : undefined;

      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
        label: 'Chat_LearnMore_Topics_click',
        link_label: _link?.innerText || '',
        link_url: _link?.href || '',
      });
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const elementsToWatch: ElementWatchItem[] = [
      { eventName: 'sendChat', eventType: 'pointerdown', index: 0, selector: '#btnSend' },
      { eventName: 'enterSend', eventType: 'keydown', index: 0, selector: '#input' },
      { eventName: 'downloadChat', eventType: 'click', index: 0, selector: '#downloadChat' },
      { eventName: 'thumbsUp', eventType: 'click', index: 0, selector: '.feedbackIcon' },
      { eventName: 'thumbsDown', eventType: 'click', index: 1, selector: '.feedbackIcon' },
      { eventName: 'feedbackSubmitBtn', eventType: 'click', index: 1, selector: '.modalButton' },
      { eventName: 'learnMore', eventType: 'click', index: 0, selector: '.referenceTitle' },
      {
        eventName: 'learnMoreTopicLink',
        eventType: 'click',
        index: 'all',
        selector: '.referenceLink',
      },
    ];

    const attached = new Map();
    const conversationListItemHandlers = new Map<Element, EventListener>();

    const handlerFactory = (item: ElementWatchItem, index?: number) => {
      return (event: Event) => {
        if (item.eventName === 'enterSend') {
          const keyboardEvent = event as KeyboardEvent;
          const target = event.target as HTMLTextAreaElement;
          if (
            keyboardEvent.key === 'Enter' &&
            !keyboardEvent.shiftKey &&
            target?.value?.trim().length > 0
          ) {
            chatToGTM(item.eventName);
          }

          return;
        }

        if (item.eventName === 'sendChat') {
          const chatInput = document.querySelector('#input') as HTMLTextAreaElement | null;
          if (!chatInput || chatInput?.value?.trim()?.length === 0) return;
        }

        if (
          item.eventName === 'downloadChat' &&
          document.querySelectorAll('.questionRow').length === 0
        ) {
          return;
        }

        if (item.eventName.includes('thumb')) {
          thumbRef.current = item.eventName;
        }

        if (item.eventName === 'learnMoreTopicLink') {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          learnMoreTopicLinkIndex.current = index!;
        }

        chatToGTM(item.eventName);
      };
    };

    const observer = new MutationObserver(() => {
      const conversationListWrapper = document.querySelector('#conversationListWrapper');
      if (conversationListWrapper) {
        const listItems = conversationListWrapper.querySelectorAll('.list-item > span');

        for (const listItem of listItems) {
          if (!conversationListItemHandlers.has(listItem)) {
            const handler = (event: Event) => {
              // check if the click is on the list item itself and not on a child element
              if (event.target !== listItem) return;

              const toggleButton = document.querySelector(
                '.betty-side-menu-icon .menuButtonClosed'
              ) as null | SVGElement;
              if (toggleButton) {
                toggleButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
              }
            };

            listItem.addEventListener('click', handler);
            conversationListItemHandlers.set(listItem, handler);
          }
        }
      }

      // eslint-disable-next-line unicorn/no-array-for-each
      elementsToWatch.forEach((item: ElementWatchItem) => {
        if (item.index === 'all') {
          const links = document.querySelectorAll(item.selector);

          for (const [index, link] of links.entries()) {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const key = `${item.selector}-${index}`;
            if (!attached.has(key)) {
              const handler = handlerFactory(item, index);
              link.addEventListener(item.eventType, handler);
              attached.set(key, handler);
            }
          }

          return;
        }

        const nodes = document.querySelectorAll(item.selector);
        const element = nodes[item.index];

        const key = `${item.selector}-${item.index}`;

        if (element && !attached.has(key)) {
          const handler = handlerFactory(item);
          element.addEventListener(item.eventType, handler);
          attached.set(key, handler);
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();

      for (const [element, handler] of conversationListItemHandlers.entries()) {
        element.removeEventListener('click', handler);
      }

      for (const [key, handler] of attached.entries()) {
        const [selector, index] = key.split('-');
        const nodes = document.querySelectorAll(selector);
        const element = index === 'all' ? null : nodes[Number(index)];
        if (element) {
          // eslint-disable-next-line id-length, unicorn/prevent-abbreviations
          const item = elementsToWatch.find((e) => e.selector === selector);
          if (item) element.removeEventListener(item.eventType, handler);
        }
      }
    };
  }, []);

  return (
    <>
      <Script async src="https://files.bettybot.ai/ui-widget/betty-bot-ui.js" />
      <link href="https://files.bettybot.ai/ui-widget/betty-bot-ui.css" rel="stylesheet" />
      {(isMounted && (
        <betty-bot
          api-endpoint-token={apiToken}
          api-url={blockData.BettyApiUrl}
          assistant-image={blockData.AssistantImage?.Url}
          className={`betty-chat ${blockData.ChatIconAlignment}`}
          cursor-class="thinking-text"
          disable-download={blockData.DisableDownload ? '1' : '0'}
          display-conversation-list={blockData.ShowConversationList ? '1' : '0'}
          display-file-upload={shouldShowUpload ? '1' : '0'}
          expand-conversation-history-by-default="1"
          info-html={isMember ? blockData.InfoHtmlMembers : blockData.InfoHtml}
          input-placeholder={blockData.InputPlaceholder}
          lower-html={blockData.LowerHtml}
          references-section-collapsed={blockData.ReferencesSectionCollapsed?.toString()}
          references-section-title={blockData.ReferencesSectionTitle}
          sidebar-position-start="closed"
          stream={blockData.ShouldStream ? '1' : '0'}
          terms-url={blockData.TermsUrl}
          user-id={userId}
          user-image={blockData.UserImage?.Url}
        />
      )) || <Loader className={styles['betty-bot-block__loader']} isInner />}
    </>
  );
};
